/**
 * useAttentionGuard — privacy-preserving client-side eye-anchor monitor
 * for the Rapid Installation step.
 *
 * Behaviour:
 *   - Lazy-imports @vladmandic/face-api ONLY when this hook is enabled
 *     (no bundle cost when the user has opted out).
 *   - Loads tinyFaceDetector weights from /face-models (vendored, ~190KB).
 *   - Acquires a low-resolution (160×120) front-camera stream.
 *   - Runs detection every 700ms.
 *   - Reports a `drift` flag when the user has been looking away (face
 *     missing OR face center > 30% off-frame) for >3000ms continuously.
 *   - Camera frames NEVER leave the browser. No recording. No upload.
 *   - Tears down camera + stops detection when disabled or unmounted.
 *
 * Returns:
 *   { ready, error, drift, attentive, drifts }
 */
import { useEffect, useRef, useState } from "react";

const MODEL_URL = "/face-models";
const DETECT_INTERVAL_MS = 700;
const DRIFT_THRESHOLD_MS = 3000;
// Off-center threshold: face bounding box center may deviate this much
// (as a fraction of frame size) before we consider the user "looking away".
const OFFCENTER_RATIO = 0.30;

export function useAttentionGuard({ enabled }) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [drift, setDrift] = useState(false);
  const [attentive, setAttentive] = useState(true);
  const [drifts, setDrifts] = useState(0);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectIdRef = useRef(null);
  const driftStartRef = useRef(null);
  const faceapiRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const start = async () => {
      try {
        // Lazy-load — only paid for when the user opts in.
        const faceapi = await import("@vladmandic/face-api");
        if (cancelled) return;
        faceapiRef.current = faceapi;

        // Load tinyFaceDetector from the vendored URL.
        await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
        if (cancelled) return;

        // Acquire a tiny camera stream (low CPU, low bandwidth).
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 160 },
            height: { ideal: 120 },
            facingMode: "user",
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;

        // Hidden offscreen video element to draw frames into.
        const video = document.createElement("video");
        video.muted = true;
        video.playsInline = true;
        video.srcObject = stream;
        await video.play();
        videoRef.current = video;

        const detector = new faceapi.TinyFaceDetectorOptions({
          inputSize: 160,
          scoreThreshold: 0.45,
        });

        setReady(true);

        // Detection loop
        detectIdRef.current = setInterval(async () => {
          if (cancelled || !videoRef.current) return;
          let detection = null;
          try {
            detection = await faceapi.detectSingleFace(videoRef.current, detector);
          } catch (e) {
            // Frame may not be ready — skip this tick.
            return;
          }

          let lookingAway = true;
          if (detection?.box) {
            const { x, y, width, height } = detection.box;
            const fw = videoRef.current.videoWidth || 160;
            const fh = videoRef.current.videoHeight || 120;
            const cx = x + width / 2;
            const cy = y + height / 2;
            const dx = Math.abs(cx - fw / 2) / fw;
            const dy = Math.abs(cy - fh / 2) / fh;
            lookingAway = dx > OFFCENTER_RATIO || dy > OFFCENTER_RATIO;
          }

          if (lookingAway) {
            if (driftStartRef.current === null) {
              driftStartRef.current = Date.now();
            }
            const ms = Date.now() - driftStartRef.current;
            if (ms >= DRIFT_THRESHOLD_MS && attentive) {
              setAttentive(false);
              setDrift(true);
              setDrifts((c) => c + 1);
              // Auto-clear drift signal after ~1.6s so the dot can pulse once
              // and then return to baseline.
              setTimeout(() => setDrift(false), 1600);
            }
          } else {
            driftStartRef.current = null;
            if (!attentive) setAttentive(true);
          }
        }, DETECT_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || "Camera access denied");
        setReady(false);
      }
    };

    start();

    return () => {
      cancelled = true;
      if (detectIdRef.current) clearInterval(detectIdRef.current);
      detectIdRef.current = null;
      if (streamRef.current) {
        try {
          streamRef.current.getTracks().forEach((t) => t.stop());
        } catch {}
      }
      streamRef.current = null;
      videoRef.current = null;
      driftStartRef.current = null;
      setReady(false);
      setDrift(false);
      setAttentive(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { ready, error, drift, attentive, drifts };
}
